// GtCombo — searchable single-select. GtSelect is fine for five options and
// unusable at fifty, so anything with a long controlled vocabulary uses this.
//
// Two modes:
//   closed (default) — the value must come from the list
//   allowFree        — a typed value not on the list is accepted, for fields
//                      that are free text in the database
//
// onCreate turns the typed text into a real record. It is given the raw text
// and must resolve to the value to select, or reject with a message.
import { useState, useRef, useEffect, useMemo } from 'react';

export default function GtCombo({
  value,
  options,
  onChange,
  placeholder = 'Select',
  allowFree = false,
  onCreate = null,
  createLabel = 'Add',
  // Extra fields captured at create time. Some records are useless without
  // them: a client with no website cannot be swept for intelligence and cannot
  // have a public blurb written, so capturing it later means it never happens.
  createFields = null,
  width,
  disabled = false,
  emptyText = 'No matches',
  hint = null,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [making, setMaking] = useState(false);
  const [draft, setDraft] = useState({});
  const wrap = useRef(null);
  const input = useRef(null);
  const listRef = useRef(null);

  const opts = useMemo(() => (options || []).map(o =>
    typeof o === 'object' ? o : { value: o, label: String(o) }), [options]);

  const current = opts.find(o => String(o.value) === String(value ?? ''));
  // A free-text value that is not on the list is still a real value, so it is
  // shown as-is rather than falling back to the placeholder.
  const shown = current ? current.label : (allowFree && value ? String(value) : '');

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => { if (open && input.current) input.current.focus(); }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return opts;
    // Prefix matches first: typing "con" should reach Construction before
    // Telecommunications.
    const starts = [], contains = [];
    for (const o of opts) {
      const l = String(o.label).toLowerCase();
      if (l.startsWith(needle)) starts.push(o);
      else if (l.includes(needle)) contains.push(o);
    }
    return [...starts, ...contains];
  }, [q, opts]);

  useEffect(() => { setActive(0); }, [q, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('.gtcb-item--active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const typed = q.trim();
  const exact = filtered.some(o => String(o.label).toLowerCase() === typed.toLowerCase());
  const canCreate = !!onCreate && typed.length > 0 && !exact;
  const canFree = allowFree && !onCreate && typed.length > 0 && !exact;
  const extra = canCreate || canFree ? 1 : 0;

  function commit(o) {
    onChange(o.value);
    setOpen(false);
    setQ('');
  }

  async function commitTyped() {
    if (canFree) { onChange(typed); setOpen(false); setQ(''); return; }
    if (!canCreate || busy) return;
    // With extra fields the row opens a form rather than creating immediately.
    if (createFields && createFields.length && !making) {
      setDraft({});
      setMaking(true);
      return;
    }
    setBusy(true);
    try {
      const v = await onCreate(typed, draft);
      if (v !== undefined && v !== null) onChange(v);
      setOpen(false);
      setQ('');
      setMaking(false);
    } finally { setBusy(false); }
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length + extra - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active < filtered.length) { if (filtered[active]) commit(filtered[active]); }
      else commitTyped();
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setQ(''); }
  }

  let lastGroup = null;

  return (
    <div className={'gtcb-wrap' + (disabled ? ' gtcb-wrap--off' : '')} ref={wrap} style={width ? { width } : undefined}>
      <button type="button" className={'gtcb-btn' + (open ? ' gtcb-btn--open' : '')}
        disabled={disabled} onClick={() => setOpen(o => !o)}>
        <span className={shown ? 'gtcb-val' : 'gtcb-ph'}>{shown || placeholder}</span>
        {shown && !disabled ? (
          <span className="gtcb-clear" role="button" tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange(''); }}>{'\u00D7'}</span>
        ) : null}
        <span className="gtcb-chev">{'\u25BE'}</span>
      </button>

      {open && making && (
        <div className="gtcb-pop">
          <div className="gtcb-make">
            <div className="gtcb-make-h">{createLabel}</div>
            <label className="gtcb-make-f"><span>Name</span><input value={typed} readOnly /></label>
            {createFields.map(f => (
              <label key={f.key} className="gtcb-make-f">
                <span>{f.label}{f.required ? '' : ' (optional)'}</span>
                <input value={draft[f.key] || ''} placeholder={f.placeholder || ''}
                  onChange={e => setDraft(x => ({ ...x, [f.key]: e.target.value }))} />
              </label>
            ))}
            {createFields.some(f => f.hint) ? <div className="gtcb-hint">{createFields.find(f => f.hint).hint}</div> : null}
            <div className="gtcb-make-a">
              <button type="button" className="gtc-btn-ghost" disabled={busy}
                onClick={() => { setMaking(false); setDraft({}); }}>Cancel</button>
              <button type="button" className="gtc-primary" disabled={busy || createFields.some(f => f.required && !String(draft[f.key] || '').trim())}
                onClick={commitTyped}>{busy ? 'Adding' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {open && !making && (
        <div className="gtcb-pop">
          <input ref={input} className="gtcb-search" value={q} placeholder="Type to search"
            onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown} spellCheck={false} />
          <div className="gtcb-list" ref={listRef}>
            {filtered.map((o, i) => {
              const head = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group || lastGroup;
              const on = String(o.value) === String(value ?? '');
              return (
                <div key={String(o.value)}>
                  {head ? <div className="gtcb-group">{head}</div> : null}
                  <div className={'gtcb-item' + (on ? ' gtcb-item--on' : '') + (i === active ? ' gtcb-item--active' : '')}
                    onMouseEnter={() => setActive(i)} onClick={() => commit(o)}>
                    <span>{o.label}</span>
                    {on ? <span className="gtcb-check">{'\u2713'}</span> : null}
                  </div>
                </div>
              );
            })}
            {!filtered.length && !canCreate && !canFree ? <div className="gtcb-empty">{emptyText}</div> : null}
            {canCreate || canFree ? (
              <div className={'gtcb-item gtcb-item--make' + (active === filtered.length ? ' gtcb-item--active' : '')}
                onMouseEnter={() => setActive(filtered.length)} onClick={commitTyped}>
                {busy ? 'Adding' : (canFree ? 'Use "' + typed + '"' : createLabel + ' "' + typed + '"')}
              </div>
            ) : null}
          </div>
          {hint ? <div className="gtcb-hint">{hint}</div> : null}
        </div>
      )}
    </div>
  );
}
